export function isKeyedSource(s) {
    return s.kind === 'keyed';
}
export function isFrameworkSource(s) {
    return s.kind === 'framework';
}
export function isBaselineSource(s) {
    return s.kind === undefined || s.kind === 'baseline';
}
/** The raw value a selector reads off a profile (undefined when absent). */
export function readProfileSelector(sel, profile) {
    if (sel.column)
        return profile[sel.column] ?? undefined;
    return profile.options?.[sel.option];
}
function selectorLabel(sel) {
    return sel.column ? sel.column : sel.option;
}
/** The selector a baseline source reads its baseline from. */
export function baselineSelector(s) {
    if (s.baselineColumn)
        return { column: s.baselineColumn };
    return { option: s.baselineOption };
}
/**
 * The leaf source that applies to this profile (keyed sources resolved).
 * Undefined when there is no source. Throws when a keyed source's selector
 * names no branch and there is no default.
 */
export function selectRequirementSourceLeaf(source, profile) {
    if (!source)
        return undefined;
    if (!isKeyedSource(source))
        return source;
    const raw = readProfileSelector(source.by, profile);
    const key = typeof raw === 'string' && raw ? raw : source.default;
    const leaf = key !== undefined ? source.sources[key] : undefined;
    if (!leaf)
        throw new Error(`no requirement source for ${selectorLabel(source.by)} ${String(raw)}`);
    return leaf;
}
/** The framework a leaf reads: its own `frameworkId`, else the manifest's. */
export function leafFrameworkId(leaf, manifestFrameworkId) {
    return leaf?.frameworkId ?? manifestFrameworkId;
}
/** The source that applies to one profile of a program (throws as `selectRequirementSourceLeaf`). */
export function effectiveRequirementSource(m, profile) {
    const leaf = selectRequirementSourceLeaf(m.requirementSource, profile);
    return { frameworkId: leafFrameworkId(leaf, m.frameworkId), leaf };
}
/**
 * True when some leaf of the program's source is a baseline with a tier option (GovRAMP): its
 * readiness is the tier being assessed (the tier overview). Programs with a framework or keyed
 * source (SOC 2, FedRAMP since v10.6) or none are read through framework / objective readiness.
 */
export function hasTieredRequirementSource(m) {
    const rs = m?.requirementSource;
    if (!rs)
        return false;
    return (isKeyedSource(rs) ? Object.values(rs.sources) : [rs]).some((l) => isBaselineSource(l) && !!l.tierOption);
}
/**
 * Every framework a program's profiles can read, in declaration order
 * (profile-less callers: the test library's objective list). The manifest's
 * own `frameworkId` first.
 */
export function requirementSourceFrameworks(m) {
    const out = [];
    const add = (f) => { if (f && !out.includes(f))
        out.push(f); };
    add(m.frameworkId);
    const rs = m.requirementSource;
    if (rs)
        for (const leaf of isKeyedSource(rs) ? Object.values(rs.sources) : [rs])
            add(leaf.frameworkId ?? m.frameworkId);
    // A keyed source whose every branch names its own framework doesn't read the manifest's.
    if (rs && isKeyedSource(rs) && m.frameworkId && Object.values(rs.sources).every((l) => l.frameworkId && l.frameworkId !== m.frameworkId)) {
        out.splice(out.indexOf(m.frameworkId), 1);
    }
    return out;
}
function parseJsonObject(json) {
    if (!json)
        return null;
    try {
        const v = JSON.parse(json);
        return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
    }
    catch {
        return null;
    }
}
const valuesOf = (v) => (Array.isArray(v) ? v : [v]).filter((x) => typeof x === 'string');
/** The scope filter a profile's options select; null when the manifest declares none (keep everything). */
export function applicabilityScope(a, options) {
    if (!a)
        return null;
    const chosen = new Set([...(a.always ?? []), ...valuesOf(options[a.option])]);
    const anyChosen = (v) => v === undefined || v === null || valuesOf(v).some((x) => chosen.has(x));
    return {
        requirement(json) {
            const o = a.requirementKey ? parseJsonObject(json) : null;
            return !o || anyChosen(o[a.requirementKey]);
        },
        objective(json) {
            const o = parseJsonObject(json);
            if (!o)
                return true;
            if (a.objectiveKey && !anyChosen(o[a.objectiveKey]))
                return false;
            for (const mt of a.match ?? []) {
                const ov = o[mt.objectiveKey];
                const pv = options[mt.option];
                if (ov === undefined || ov === null || ov === mt.any || pv === undefined || pv === null || pv === mt.any)
                    continue;
                if (!valuesOf(ov).some((x) => valuesOf(pv).includes(x)))
                    return false;
            }
            return true;
        },
    };
}
/** Validation for `readiness.applicability`. */
export function applicabilityProblems(m) {
    const a = m.readiness.applicability;
    if (!a)
        return [];
    const errs = [];
    if (m.readiness.objectiveMode !== 'objectives')
        errs.push(`${m.key}: readiness.applicability needs objectiveMode 'objectives'`);
    if (!m.profile.options)
        errs.push(`${m.key}: readiness.applicability reads profile options, so the profile needs options`);
    if (!a.option.trim())
        errs.push(`${m.key}: readiness.applicability needs an option`);
    if (!a.requirementKey && !a.objectiveKey && !(a.match ?? []).length)
        errs.push(`${m.key}: readiness.applicability filters nothing (no requirementKey, objectiveKey or match)`);
    return errs;
}
/**
 * ADR-0079 §5: a profile whose tier its baseline doesn't offer (GovRAMP Core
 * at Low). Returns the message, or null. Runs after the profile options parse;
 * a profile that selects no source is left to the readers (unchanged).
 */
export function requirementSourceProfileProblem(m, profile) {
    let leaf;
    try {
        leaf = selectRequirementSourceLeaf(m.requirementSource, profile);
    }
    catch {
        return null;
    }
    if (!leaf || !isBaselineSource(leaf) || !leaf.allowedTiers || !leaf.tierOption)
        return null;
    const sel = baselineSelector(leaf);
    const v = readProfileSelector(sel, profile);
    const tier = profile.options?.[leaf.tierOption];
    if (typeof v !== 'string' || typeof tier !== 'string')
        return null;
    const allowed = leaf.allowedTiers[v];
    if (!allowed || allowed.includes(tier))
        return null;
    return `The ${tier} tier is not available at ${selectorLabel(sel).replace(/_/g, ' ')} ${v}; choose ${allowed.join(', ')}.`;
}
/** Problems with a manifest's `requirementSource`; used by `validateProgramManifest`. */
export function requirementSourceProblems(m) {
    const rs = m.requirementSource;
    if (!rs)
        return [];
    const errs = [];
    const optionUsed = (sel) => !!sel && !sel.column;
    const checkSelector = (sel, what) => {
        if ((sel.column ? 1 : 0) + (sel.option ? 1 : 0) !== 1)
            errs.push(`${m.key}: ${what} names exactly one of column / option`);
        if (sel.column && m.profile.columns[sel.column]?.rule !== 'required') {
            errs.push(`${m.key}: ${what} reads column ${sel.column}, which the profile must require`);
        }
    };
    let needsOptions = false;
    const checkLeaf = (leaf, where) => {
        if (leaf.frameworkId !== undefined && !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(leaf.frameworkId))
            errs.push(`${m.key}: ${where} frameworkId must be lower-case kebab`);
        if (!leafFrameworkId(leaf, m.frameworkId))
            errs.push(`${m.key}: ${where} needs a frameworkId (the manifest has none)`);
        if (isFrameworkSource(leaf)) {
            const pre = leaf.programControls?.refPrefix;
            if (pre !== undefined && !pre.trim())
                errs.push(`${m.key}: ${where} programControls.refPrefix is empty`);
            return;
        }
        const b = leaf;
        if (!!b.baselineOption === !!b.baselineColumn)
            errs.push(`${m.key}: ${where} names exactly one of baselineOption / baselineColumn`);
        else
            checkSelector(baselineSelector(b), `${where} baseline selector`);
        if (b.baselineOption || b.tierOption)
            needsOptions = true;
        if (!Object.keys(b.baselines).length)
            errs.push(`${m.key}: requirementSource needs at least one baseline`);
        for (const k of Object.values(b.baselines))
            if (!/^[a-z0-9][a-z0-9-]*$/.test(k))
                errs.push(`${m.key}: baseline key ${k} must be lower-case kebab`);
        if (b.tierAliases && !b.tierOption)
            errs.push(`${m.key}: tierAliases need a tierOption`);
        if (b.allowedTiers) {
            if (!b.tierOption)
                errs.push(`${m.key}: allowedTiers need a tierOption`);
            for (const [v, tiers] of Object.entries(b.allowedTiers)) {
                if (!b.baselines[v])
                    errs.push(`${m.key}: allowedTiers ${v} names no baseline`);
                if (!tiers.length)
                    errs.push(`${m.key}: allowedTiers ${v} allows no tier`);
            }
        }
    };
    if (m.readiness.objectiveMode !== 'objectives')
        errs.push(`${m.key}: requirementSource needs objectiveMode 'objectives'`);
    if (isKeyedSource(rs)) {
        checkSelector(rs.by, 'keyed requirementSource');
        if (optionUsed(rs.by))
            needsOptions = true;
        const keys = Object.keys(rs.sources);
        if (!keys.length)
            errs.push(`${m.key}: keyed requirementSource needs at least one source`);
        if (rs.default !== undefined && !rs.sources[rs.default])
            errs.push(`${m.key}: keyed requirementSource default ${rs.default} names no source`);
        const allowed = rs.by.column ? m.profile.columns[rs.by.column]?.values : undefined;
        for (const v of allowed ?? [])
            if (!rs.sources[v])
                errs.push(`${m.key}: keyed requirementSource has no source for ${rs.by.column} ${v}`);
        for (const k of keys)
            if (allowed && !allowed.includes(k))
                errs.push(`${m.key}: keyed requirementSource source ${k} is not a value of ${rs.by.column}`);
        for (const [k, leaf] of Object.entries(rs.sources)) {
            if (leaf.kind === 'keyed')
                errs.push(`${m.key}: keyed requirementSource ${k} cannot nest another keyed source`);
            else
                checkLeaf(leaf, `requirementSource ${k}`);
        }
    }
    else {
        checkLeaf(rs, 'requirementSource');
    }
    if (needsOptions && !m.profile.options)
        errs.push(`${m.key}: requirementSource reads profile options, so the profile needs options`);
    return errs;
}
/**
 * V106-3-2 / ADR-0079 §5: the values onboarding offers for one `askOptions` entry,
 * given the options picked so far. For the baseline's tier option, only the tiers
 * the picked baseline allows (GovRAMP Low: Ready, Authorized, Provisional); every
 * other option, or a baseline without `allowedTiers`, offers all its values.
 */
export function askOptionValues(m, key, values, picked) {
    let leaf;
    try {
        leaf = selectRequirementSourceLeaf(m.requirementSource, { options: picked });
    }
    catch {
        return [...values];
    }
    if (!leaf || !isBaselineSource(leaf) || !leaf.allowedTiers || leaf.tierOption !== key)
        return [...values];
    const v = readProfileSelector(baselineSelector(leaf), { options: picked });
    const allowed = typeof v === 'string' ? leaf.allowedTiers[v] : undefined;
    return allowed ? values.filter((x) => allowed.includes(x.value)) : [...values];
}
//# sourceMappingURL=requirement-source.js.map